import React, { useState } from 'react'
import { Plus, Minus, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Button } from '@/app/components/ui/button'
import { InventoryItem } from '@/app/services/inventory.service'

interface AdjustModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedItem: InventoryItem | null
  onApplyAdjustment: (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
  }) => void
}

const COMMON_REASONS = [
  'Merma',
  'Devolución de cliente',
  'Corrección de inventario',
  'Caducidad',
  'Robo / Pérdida',
  'Compra / Recepción',
]

export function AdjustModal({
  open,
  onOpenChange,
  selectedItem,
  onApplyAdjustment,
}: AdjustModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add')
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('')
  const [adjustmentReason, setAdjustmentReason] = useState('')

  const currentStock = selectedItem?.stock ?? 0
  const parsedQty = parseInt(adjustmentQuantity) || 0
  const newStock =
    adjustmentType === 'add' ? currentStock + parsedQty : currentStock - parsedQty
  const willGoNegative = adjustmentType === 'subtract' && newStock < 0

  const handleApply = () => {
    if (selectedItem) {
      onApplyAdjustment({
        item: selectedItem.name,
        type: adjustmentType,
        quantity: adjustmentQuantity,
        reason: adjustmentReason,
      })
    }
    setAdjustmentQuantity('')
    setAdjustmentReason('')
  }

  const handleClose = (val: boolean) => {
    if (!val) {
      setAdjustmentQuantity('')
      setAdjustmentReason('')
      setAdjustmentType('add')
    }
    onOpenChange(val)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ajuste de Stock</DialogTitle>
          <DialogDescription className="sr-only">
            Agregar o restar unidades del inventario con motivo
          </DialogDescription>
        </DialogHeader>

        {selectedItem && (
          <div className="space-y-4">
            {/* Tarjeta del producto */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 overflow-hidden">
              <p className="text-sm font-semibold text-slate-900 break-words line-clamp-2" title={selectedItem.name}>{selectedItem.name}</p>
              <p className="mt-1 text-xs text-slate-500">
                Stock actual:{' '}
                <span className="font-medium text-slate-700">{currentStock} unidades</span>
              </p>
            </div>

            {/* Tipo de ajuste */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Tipo de ajuste:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setAdjustmentType('add')}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${
                    adjustmentType === 'add'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-xs font-medium">Entrada</span>
                </button>
                <button
                  onClick={() => setAdjustmentType('subtract')}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${
                    adjustmentType === 'subtract'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Minus className="h-5 w-5" />
                  <span className="text-xs font-medium">Salida</span>
                </button>
              </div>
            </div>

            {/* Cantidad */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Cantidad:</label>
              <Input
                type="number"
                min="1"
                value={adjustmentQuantity}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '') {
                    setAdjustmentQuantity('')
                  } else {
                    setAdjustmentQuantity(String(Math.abs(parseInt(val) || 0) || ''))
                  }
                }}
                placeholder="Ej: 5"
                className={willGoNegative ? 'border-red-400 focus-visible:ring-red-400' : ''}
              />
              {willGoNegative && (
                <p className="flex items-center gap-1 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  La cantidad supera el stock disponible ({currentStock} u.)
                </p>
              )}
            </div>

            {/* Motivo */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Motivo:</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {COMMON_REASONS.map((reason) => (
                  <button
                    key={reason}
                    onClick={() => setAdjustmentReason(reason)}
                    className={`rounded-full px-2.5 py-0.5 text-xs transition-colors ${
                      adjustmentReason === reason
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <Input
                type="text"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="O escribe un motivo personalizado..."
              />
            </div>

            {/* Preview del resultado */}
            {adjustmentQuantity && parsedQty > 0 && (
              <div
                className={`rounded-lg px-4 py-3 text-sm ${
                  willGoNegative
                    ? 'bg-red-50 text-red-800'
                    : adjustmentType === 'add'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-orange-50 text-orange-800'
                }`}
              >
                <span>Nuevo stock estimado: </span>
                <strong>
                  {newStock} unidad{newStock !== 1 ? 'es' : ''}
                </strong>
              </div>
            )}

            <Button
              onClick={handleApply}
              disabled={!adjustmentQuantity || !adjustmentReason || willGoNegative || parsedQty === 0}
              className={`w-full ${
                adjustmentType === 'add'
                  ? 'bg-[#3B7D3B] hover:bg-[#2d6a2d]'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {adjustmentType === 'add' ? (
                <>
                  <Plus className="mr-1.5 h-4 w-4" /> Registrar Entrada
                </>
              ) : (
                <>
                  <Minus className="mr-1.5 h-4 w-4" /> Registrar Salida
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
